import { Boxes, BrainCircuit, Download, KeyRound, LayoutDashboard, Mic, Puzzle, Server, Trash2, type LucideIcon } from 'lucide-react';
import type { LocaleDict } from '../../lib/i18n/types';

/** Single source of truth for the Settings sections. The order defines the records the deck's own
 *  navigation draws — the secondary column and the phone's line of tabs alike — and the panels the page
 *  mounts behind them; the sidebar holds one row for the deck and nothing under it. Plugin-owned
 *  settings are contributed at runtime instead of being hard-coded here. A stale ?cat= deep-link to any
 *  removed section falls back to 'system' via the isSectionId validator, unless {@link SECTION_ALIASES}
 *  names a successor for it. */
export const SETTINGS_SECTIONS = [
  { id: 'system', icon: Server },
  { id: 'brain', icon: BrainCircuit },
  { id: 'models', icon: Boxes },
  { id: 'plugins', icon: Puzzle },
  { id: 'plugins-available', icon: Download },
  { id: 'access', icon: KeyRound },
  { id: 'dashboard', icon: LayoutDashboard },
  { id: 'voice', icon: Mic },
  { id: 'data', icon: Trash2 },
] as const;

export type SettingsCategory = (typeof SETTINGS_SECTIONS)[number]['id'];
export const SETTINGS_CATEGORY_VALUES = SETTINGS_SECTIONS.map(({ id }) => id);

/** Retired section ids and their current destination, resolved before the category validity check. */
export const SECTION_ALIASES: Record<string, SettingsCategory> = { memory: 'models' };

/** Where a settings section is addressed. The one rule, so the menu row, a cross-link and the page's own
 *  history rewrite cannot spell the same section three ways. */
export const settingsSectionHref = (id: string): string => `/settings?cat=${id}`;

export interface SettingsSectionDescriptor {
  id: SettingsCategory;
  icon: LucideIcon;
  label: string;
  description: string;
}

/** The sections with their translated names, resolved once for the two surfaces that show them: the
 *  deck's own navigation and the page's own hero. The assistant's section is named after the assistant, so
 *  its label arrives already interpolated — this module stays free of the brand. */
export function settingsSections(t: LocaleDict, agentAiLabel: string): SettingsSectionDescriptor[] {
  const hints: Record<SettingsCategory, string> = {
    system: t.settings.systemSectionHint,
    brain: t.settings.brainSectionHint,
    models: t.settings.modelsSectionHint,
    plugins: t.settings.pluginsSectionHint,
    'plugins-available': t.settings.availablePluginsSectionHint,
    access: t.settings.accessSectionHint,
    dashboard: t.settings.dashboardSectionHint,
    data: t.settings.dataSectionHint,
    voice: t.voice.settingsHint,
  };
  return SETTINGS_SECTIONS.map(({ id, icon }) => ({
    id,
    icon,
    label: id === 'brain' ? agentAiLabel : id === 'voice' ? t.voice.settingsSection : id === 'plugins-available' ? t.settings.availablePlugins : t.settings[id],
    description: hints[id],
  }));
}
